import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Form from 'components/Form';
import AccordionController from 'components/AccordionController';
import EditorJsField from 'components/EditorJsField';
import General from 'components/RoomEditMainGeneral';

import {
  EditorJsFieldType,
  useEditorJsField,
  textFieldValue,
  TranslatableField,
  useTranslatableField,
  useErrorHandler,
  makeTranslatableInput,
} from 'lib';
import { makeLabel } from 'components/RoomPropertySelect';

import { SelectValue } from 'components/Select';
import { RoomEditMain_room$key } from '__generated__/RoomEditMain_room.graphql';
import { RoomEditMain_data$key } from '__generated__/RoomEditMain_data.graphql';
import { RoomEditMainMutation } from '__generated__/RoomEditMainMutation.graphql';
import { makeEditorJsInput } from 'lib/graphql/makeEditorJsInput';

type FormValues = {
  name: TranslatableField;
  capacity: string;
  properties: readonly SelectValue[];
  descriptionText: EditorJsFieldType;
};

type Props = {
  room: RoomEditMain_room$key;
  data: RoomEditMain_data$key;
};

const RoomEditMain: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const room = useFragment(
    graphql`
      fragment RoomEditMain_room on Room {
        id
        capacity
        description

        properties {
          id
          name
          icon
          etgKey
        }

        name {
          ...useTranslatableField_text
        }

        descriptionText {
          ...EditorJsField_text
          ...useEditorJsField_text
        }

        ...RoomEditMainGeneral_room
      }
    `,
    props.room
  );

  const data = useFragment(
    graphql`
      fragment RoomEditMain_data on Query {
        ...EditorJsField_data
        ...RoomEditMainGeneral_data
        ...useTranslatableField_data
        ...useEditorJsField_data
      }
    `,
    props.data
  );

  const [update, updating] = useMutation<RoomEditMainMutation>(graphql`
    mutation RoomEditMainMutation($id: ID!, $input: UpdateRoomInput!) {
      updateRoom(id: $id, input: $input) {
        ...RoomEditTabs_room
        ...RoomSidebar_room
      }
    }
  `);

  const initial: FormValues = {
    name: useTranslatableField({ text: room.name, data }),
    capacity: textFieldValue(room.capacity),
    properties: room.properties.map(({ id, icon, ...rest }) => ({
      id,
      icon,
      name: makeLabel(rest),
    })),
    descriptionText: useEditorJsField({ text: room.descriptionText, data }),
  };

  const handleSubmit = useCallback(
    (values: FormValues) => {
      update({
        variables: {
          id: room.id,
          input: {
            name: makeTranslatableInput(values.name),
            capacity: Number(values.capacity),
            properties: values.properties.map(({ id }) => ({ id })),
            descriptionText: makeEditorJsInput(values.descriptionText),
          },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, room.id, update]
  );

  return (
    <Formik<FormValues> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <AccordionController id="roomMain">
          <General data={data} room={room} />

          <EditorJsField
            name="descriptionText"
            label="Aprašymas"
            text={room.descriptionText}
            data={data}
            fromHtml={room.description}
          />
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default RoomEditMain;
